import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import CoordinateEmpty from 'components/coordinate/CoordinateEmpty';
import CoordinateListItem, { CoordinateListItemField } from 'components/coordinate/CoordinateListItem';
import Page from 'components/layouts/page/Page';
import { simpleDate } from 'lib/date';
import { capitalize } from 'lib/util';
import {
  CoordinateSearchParams,
  CoordinateSearchResult,
  importedPatientsCoordinateSearch,
  importedPatientsQueryKeys,
} from 'services/api/importedPatients';
import { useToastActions } from 'stores/toastStore';
import { LabelBold } from 'styles/typography';

import CoordinateRequestModal from './CoordinateRequestModal';
import CoordinateRequestSearchForm from './CoordinateRequestSearchForm';

function buildFields(result: CoordinateSearchResult): CoordinateListItemField[] {
  return [
    { label: 'DOB', value: simpleDate(result.dateOfBirth) ?? '' },
    { label: 'Gender', value: capitalize(result.sex) },
    result.owner && { label: result.owner.locationType.name, value: result.owner.name },
  ].filter((field): field is CoordinateListItemField => Boolean(field));
}

function CoordinateRequestPage() {
  const [searchParams, setSearchParams] = useState<CoordinateSearchParams | null>(null);
  const [displayedSearch, setDisplayedSearch] = useState<CoordinateSearchParams | null>(null);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  const [activePatient, setActivePatient] = useState<CoordinateSearchResult | null>(null);

  const navigate = useNavigate();
  const { addToast } = useToastActions();

  const searchQuery = useQuery({
    queryKey: importedPatientsQueryKeys.coordinateSearch(searchParams!),
    queryFn: ({ signal }) => importedPatientsCoordinateSearch(searchParams!, signal),
    enabled: !!searchParams,
    placeholderData: keepPreviousData,
  });

  const results = searchParams ? searchQuery.data : undefined;
  const hasResults = results !== undefined;
  const resultCount = results?.length ?? 0;
  const resultLabel = displayedSearch
    ? `${resultCount} ${resultCount === 1 ? 'Result' : 'Results'} Found for '${displayedSearch.name}'`
    : '';

  useEffect(() => {
    if (!searchQuery.isFetching && searchParams) {
      setDisplayedSearch(searchParams);
    }
  }, [searchQuery.isFetching, searchParams]);

  // If we mount with results already in cache (e.g. back navigation), skip the
  // entrance animation.
  useEffect(() => {
    if (hasResults) setShouldAnimate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddToPortfolio = (id: string) => {
    const patient = results?.find((result) => result.id === id);
    if (patient) setActivePatient(patient);
  };

  const handleCancel = () => setActivePatient(null);

  const handleAutoAccepted = (episodeId: string) => {
    setActivePatient(null);
    navigate(`/patients/${episodeId}`);
  };

  const handleRequestPending = () => {
    if (!activePatient) return;

    const customerName = activePatient.client.name;
    setActivePatient(null);
    navigate('/coordinate/submitted', { state: { customerName } });
  };

  const handleError = () => {
    addToast({ text: 'Something went wrong. Please try again' });
  };

  return (
    <Page>
      <Content>
        <FormContainer $isCentered={!hasResults}>
          <CoordinateRequestSearchForm onSearch={setSearchParams} isLoading={searchQuery.isFetching} />
        </FormContainer>
        {hasResults && (
          <ResultsContainer $shouldAnimate={shouldAnimate}>
            {resultCount > 0 ? (
              <>
                <ResultsLabel fontSize='14'>{resultLabel}</ResultsLabel>
                <ResultsList>
                  {results!.map((result) => (
                    <CoordinateListItem
                      key={result.id}
                      id={result.id}
                      name={result.name}
                      fields={buildFields(result)}
                      tag={result.client.name}
                      onAddToPortfolio={handleAddToPortfolio}
                      disabled={result.hasPendingRequest}
                      buttonLabel={result.hasPendingRequest ? 'Request already submitted' : 'Add to Portfolio'}
                    />
                  ))}
                </ResultsList>
              </>
            ) : (
              <CoordinateEmpty message={resultLabel} />
            )}
          </ResultsContainer>
        )}
      </Content>
      {activePatient && (
        <CoordinateRequestModal
          patientName={activePatient.name}
          importedPatientId={activePatient.id}
          upstreamClientId={activePatient.client.id}
          upstreamManagedProviderTypeIds={activePatient.managedProviderTypeIds}
          onCancel={handleCancel}
          onAutoAccepted={handleAutoAccepted}
          onRequestPending={handleRequestPending}
          onError={handleError}
        />
      )}
    </Page>
  );
}

export default CoordinateRequestPage;

const Content = styled(Page.Content)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormContainer = styled.div<{ $isCentered: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;
  position: relative;
  z-index: 2;
  transition: transform 0.75s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${({ $isCentered }) => ($isCentered ? 'translateY(25vh)' : 'translateY(0)')};
`;

const ResultsContainer = styled.div<{ $shouldAnimate: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 960px;
  margin-top: 32px;
  position: relative;
  z-index: 1;
  animation: ${({ $shouldAnimate }) =>
    $shouldAnimate ? 'fadeSlideIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.5s both' : 'none'};

  @keyframes fadeSlideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ResultsLabel = styled(LabelBold)`
  margin-bottom: 4px;
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
